import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  PhysioValueProps,
  SwimInputData,
  BikeInputData,
  RunInputData,
} from '@/api/physioValueService';
import type { UserProps } from '@/api/usersService';
import type { SportType } from '@/types/planTypes';
import { toggleSportRpe } from '@/api/usersService';
import { queryKeys } from '@/lib/queryKeys';
import { sportColors } from '@/lib/sportColors';
import Switch from '@/components/ui/Switch';
import {
  SwimTestInput,
  BikeTestInput,
  RunTestInput,
  SwimDirectInput,
  BikeDirectInput,
  RunDirectInput,
} from './CriticalTestInput';

interface EditPhysioValuesProps {
  user: UserProps;
  data: PhysioValueProps;
  updateData: (newData: Partial<PhysioValueProps>) => void;
}

type InputMode = 'test' | 'direct';

const EditPhysioValues = ({ user, data, updateData }: EditPhysioValuesProps) => {
  const queryClient = useQueryClient();

  const [swimInputData, setSwimInputData] = useState<SwimInputData>(
    data.swim_input_data || { distance1: 200, time1: 0, distance2: 400, time2: 0 }
  );
  const [bikeInputData, setBikeInputData] = useState<BikeInputData>(
    data.bike_input_data || { power1: 0, time1: 720, power2: 0, time2: 180 }
  );
  const [runInputData, setRunInputData] = useState<RunInputData>(
    data.run_input_data || { distance1: 0, time1: 180, distance2: 0, time2: 720 }
  );

  const [swimMode, setSwimMode] = useState<InputMode>(data.swim_input_data ? 'test' : 'direct');
  const [bikeMode, setBikeMode] = useState<InputMode>(data.bike_input_data ? 'test' : 'direct');
  const [runMode, setRunMode] = useState<InputMode>(data.run_input_data ? 'test' : 'direct');

  const [swimDirectValue, setSwimDirectValue] = useState<number>(data.css_swim || 0);
  const [bikeDirectValue, setBikeDirectValue] = useState<number>(data.cp || 0);
  const [runDirectValue, setRunDirectValue] = useState<number>(data.cs || 0);

  const [localRpe, setLocalRpe] = useState<Record<SportType, boolean>>({
    swimming: !!user.rpe_swimming,
    cycling: !!user.rpe_cycling,
    running: !!user.rpe_running,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      updateData({
        swim_input_data: swimMode === 'test' ? swimInputData : null,
        css_swim: swimMode === 'direct' ? swimDirectValue : undefined,
        bike_input_data: bikeMode === 'test' ? bikeInputData : null,
        cp: bikeMode === 'direct' ? bikeDirectValue : undefined,
        run_input_data: runMode === 'test' ? runInputData : null,
        cs: runMode === 'direct' ? runDirectValue : undefined,
      });
    }, 100);
    return () => clearTimeout(handler);
  }, [
    swimInputData,
    swimMode,
    swimDirectValue,
    bikeInputData,
    bikeMode,
    bikeDirectValue,
    runInputData,
    runMode,
    runDirectValue,
    updateData,
  ]);

  const handleToggleRpe = async (sport: SportType, checked: boolean) => {
    setLocalRpe((prev) => ({ ...prev, [sport]: checked }));
    await toggleSportRpe(sport as 'swimming' | 'cycling' | 'running', checked, user.user_id);
    queryClient.invalidateQueries({ queryKey: queryKeys.userPhysio(user.user_id) });
  };

  const fields = [
    {
      key: 'swimming' as SportType,
      emoji: '🏊',
      label: 'Natation',
      mode: swimMode,
      setMode: setSwimMode,
      test: <SwimTestInput value={swimInputData} onChange={setSwimInputData} />,
      direct: <SwimDirectInput value={swimDirectValue} onChange={setSwimDirectValue} />,
    },
    {
      key: 'cycling' as SportType,
      emoji: '🚴',
      label: 'Cyclisme',
      mode: bikeMode,
      setMode: setBikeMode,
      test: <BikeTestInput value={bikeInputData} onChange={setBikeInputData} />,
      direct: <BikeDirectInput value={bikeDirectValue} onChange={setBikeDirectValue} />,
    },
    {
      key: 'running' as SportType,
      emoji: '🏃',
      label: 'Course à pied',
      mode: runMode,
      setMode: setRunMode,
      test: <RunTestInput value={runInputData} onChange={setRunInputData} />,
      direct: <RunDirectInput value={runDirectValue} onChange={setRunDirectValue} />,
    },
  ];

  return (
    <div className="flex w-full flex-col gap-4">
      {fields.map((field) => {
        const rpeEnabled = localRpe[field.key];
        return (
          <div key={field.key} className="w-full rounded-xl bg-gray-100 p-4 dark:bg-gray-800">
            <div className={`flex items-center justify-between ${rpeEnabled ? '' : 'mb-4'}`}>
              <div className="flex items-center gap-2">
                <span
                  className="rounded-lg px-2 py-1 text-xl"
                  style={{ backgroundColor: sportColors[field.key] }}
                >
                  {field.emoji}
                </span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{field.label}</span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">RPE</span>
                <Switch size="sm" checked={rpeEnabled} onChange={(c) => handleToggleRpe(field.key, c)} />
              </div>
            </div>

            {!rpeEnabled && (
              <>
                <div className="mb-4 flex items-center gap-2">
                  <span
                    className={`text-sm ${field.mode === 'test' ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-500'}`}
                  >
                    Tests
                  </span>
                  <Switch
                    size="sm"
                    checked={field.mode === 'direct'}
                    onChange={(c) => field.setMode(c ? 'direct' : 'test')}
                  />
                  <span
                    className={`text-sm ${field.mode === 'direct' ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-500'}`}
                  >
                    Saisie directe
                  </span>
                </div>
                {field.mode === 'test' ? field.test : field.direct}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EditPhysioValues;
